/**
 * Catchball vocabulary — the ONE set of product-facing words for each act in
 * the ball-passing loop (canon: docs/1-canon/01-terminology-and-glossary.md
 * "Catchball", "Ball custody within a Loop", canon 29). An audit
 * (docs/9-exploration audits) found the same act rendered under many
 * different labels across the app ("Send Requests" / "Review & send",
 * "Grab the ball" / "Pick up the ball", "Send this up" / "Submit Proposal
 * Packet" / "Send proposal to <name>", "Aggregate" / "≈ approve", …).
 * Import these constants instead of writing a new string.
 *
 * iOS keeps its own copy in sync by hand: apps/ios/Priorities/CatchballVocabulary.swift.
 * Do not edit that file from here — just keep meanings aligned.
 */

export const CATCHBALL_LABELS = {
  // ── Downward: start a Loop / send a Request ──────────────────────────────
  /**
   * canon verb: toss (the downward Request handoff that opens a Loop) — the
   * SAME label for every toss, regardless of which level it lands on (owner
   * ruling 2026-09-22, #14: one button everywhere a toss happens, then a
   * level chooser names the destination — no more per-level button text).
   */
  sendTheBall: 'Toss the ball',
  /** Same act, used while a draft still needs a look before it goes out. */
  reviewAndSend: 'Review & send',
  /** Same act, first time a Loop's Request goes out. */
  sendFirstRequest: 'Send first request',

  // ── Claim / release custody ───────────────────────────────────────────────
  /** canon: claim — taking personal custody of an unclaimed Ball. */
  takeTheBall: 'Take the ball',
  /** canon: release — handing custody back to "unclaimed". */
  releaseTheBall: 'Release the ball',
  /**
   * canon: Turn — opening the first Turn at a Path Node when no Ball has
   * arrived yet (e.g. the Path anchor starting a Loop). Deliberately NOT
   * "Pick up the ball" — that phrasing reads as a claim (Take the ball),
   * and this is a different act: there is no Ball in play yet to claim.
   */
  startTurn: "Start this level's turn",

  // ── Move between levels ───────────────────────────────────────────────────
  /** canon: Split / Handoff, downward. */
  passDown: 'Pass down',
  /** canon: Handoff, upward (Proposal Submission when a Proposal is attached). */
  passUp: 'Pass up',

  // ── Fan out / merge (canon: Split / Aggregate, canon 29 §3) ──────────────
  /** canon verb: Split — the holder fans the Request into parallel child Balls. */
  fanOut: 'Fan out',
  /** canon verb: Aggregate — merging sibling Balls' outputs into the parent's payload. */
  combineResponses: 'Combine responses',

  // ── Submit (Proposal Submission handoff) ──────────────────────────────────
  /** canon: Proposal Submission — the Ball carries a Proposal artifact upward. */
  submitProposal: 'Submit proposal',

  // ── Decide (Catchball Resolution) ─────────────────────────────────────────
  accept: 'Accept',
  acceptWithConditions: 'Accept with conditions',
  returnForRevision: 'Return for revision',
  reject: 'Reject',
  /** Accept where the same action also writes the Plan of Record — verify
   *  against the RPC called before using this label; otherwise use `accept`. */
  acceptAndRatify: 'Accept & ratify',
} as const;

export type CatchballLabelKey = keyof typeof CATCHBALL_LABELS;
